<template>
	<v-row class="full-height system-category-page">
		<v-col cols="12" class="flex-none pb-0">
			<v-card class="system-category-toolbar-card" flat>
				<v-card-text class="pa-3">
					<v-row align="center" dense>
						<!-- Search -->
						<v-col cols="12" sm="6" md="4" lg="3">
							<v-text-field
								v-model="searchKeywords"
								placeholder="Search system categories..."
								prepend-inner-icon="mdi-magnify"
								outlined
								dense
								hide-details
								clearable
								class="system-category-search"
								@input="onSearchInput"
								@click:clear="onSearchClear"
							></v-text-field>
						</v-col>

						<v-spacer class="d-none d-md-flex"></v-spacer>

						<!-- Delete Button (visible when items selected) -->
						<v-col cols="auto" v-if="deleteCategories.length > 0" class="mr-2">
							<v-btn depressed rounded color="error" @click="deleteWarning = true">
								<v-icon left>mdi-delete</v-icon>
								Delete ({{ deleteCategories.length }})
							</v-btn>
						</v-col>

						<!-- New category -->
						<v-col cols="auto">
							<v-btn depressed rounded color="success" @click="newCategory()" :disabled="editorOpen">
								<v-icon left>mdi-plus</v-icon>
								New category
							</v-btn>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-col>

		<v-col cols="12" class="flex-auto pt-0 system-category-table-container">
			<v-data-table
				:headers="headers"
				:items="categories"
				:options.sync="tableOptions"
				:server-items-length="totalCount"
				:loading="$apollo.queries.systemCategoriesData.loading"
				:footer-props="footerProps"
				:mobile-breakpoint="0"
				class="system-category-data-table"
				fixed-header
				show-select
				v-model="selectedCategories"
				item-key="systemCategoryId"
				@click:row="editCategory"
			>
				<template v-slot:loading>
					<v-skeleton-loader type="table-row@10"></v-skeleton-loader>
				</template>

				<template v-slot:no-data>
					<div class="text-center py-8">
						<v-icon size="64" color="grey lighten-1">mdi-shape</v-icon>
						<p class="mt-4 text-h6 grey--text">No system categories found</p>
					</div>
				</template>

				<template v-slot:item.icon="{ item }">
					<div class="table-icon-tile">
						<SystemCategoryIcon :system-category-id="item.systemCategoryId" />
					</div>
				</template>

				<template v-slot:item.name="{ item }">
					<span class="category-name">{{ item.name }}</span>
				</template>

				<template v-slot:item.type="{ item }">
					<span class="category-type">{{ typeLabel(item.type) }}</span>
				</template>

				<template v-slot:item.behaviors="{ item }">
					<div class="behavior-tags">
						<template v-for="behavior in enabledBehaviors(item)">
							<v-tooltip bottom :key="behavior.key" max-width="320">
								<template v-slot:activator="{ on, attrs }">
									<span class="behavior-tag" v-bind="attrs" v-on="on">{{ behavior.tag }}</span>
								</template>
								<span>{{ behavior.description }}</span>
							</v-tooltip>
						</template>
						<span v-if="enabledBehaviors(item).length === 0" class="behavior-none">Default</span>
					</div>
				</template>

				<template v-slot:item.actions="{ item }">
					<v-btn icon color="primary" :disabled="editorOpen" title="Edit" @click.stop="editCategory(item)">
						<v-icon>mdi-pencil</v-icon>
					</v-btn>
				</template>
			</v-data-table>
		</v-col>

		<!-- Editor drawer: one form for new and existing categories, nothing edits inline in the table -->
		<v-navigation-drawer
			v-model="editorOpen"
			fixed
			right
			temporary
			width="480"
			class="category-editor"
			@input="onEditorToggle"
		>
			<div v-if="editor" class="category-editor__inner">
				<header class="category-editor__header">
					<div>
						<p class="category-editor__eyebrow">{{ editor.systemCategoryId ? "Edit category" : "New category" }}</p>
						<h2 class="category-editor__title">{{ editor.name || "Untitled category" }}</h2>
					</div>
					<v-btn icon title="Close" @click="closeEditor">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</header>

				<div class="category-editor__body">
					<v-alert v-if="saveError" type="error" dense text class="mb-4">{{ saveError }}</v-alert>

					<!-- Icon -->
					<section class="editor-section">
						<div class="editor-icon">
							<div
								class="editor-dropzone"
								:class="{ 'editor-dropzone--active': dragActive, 'editor-dropzone--empty': !iconPreview && !hasCurrentIcon }"
								role="button"
								tabindex="0"
								title="Upload icon"
								@click="openFilePicker"
								@keydown.enter.prevent="openFilePicker"
								@keydown.space.prevent="openFilePicker"
								@dragover.prevent="dragActive = true"
								@dragleave.prevent="dragActive = false"
								@drop.prevent="onIconDrop"
							>
								<img v-if="iconPreview" :src="iconPreview" alt="Icon preview" class="editor-dropzone__image" />
								<SystemCategoryIcon
									v-else-if="hasCurrentIcon"
									:key="`editor-${editor.systemCategoryId}-${editor.iconVersion}`"
									:system-category-id="editor.systemCategoryId"
									class="editor-dropzone__current"
								/>
								<div v-else class="editor-dropzone__placeholder">
									<v-icon size="28">mdi-image-plus</v-icon>
									<span>Add icon</span>
								</div>
							</div>
							<div class="editor-icon__controls">
								<p class="editor-icon__label">Icon</p>
								<p class="editor-icon__hint">SVG or PNG, up to 256 KB. Drop a file on the tile or choose one.</p>
								<div class="editor-icon__buttons">
									<v-btn small depressed color="primary" @click="openFilePicker">
										<v-icon left small>mdi-upload</v-icon>
										{{ iconPreview || hasCurrentIcon ? "Replace" : "Choose file" }}
									</v-btn>
									<v-btn v-if="iconPreview" small text @click="clearPendingIcon">Undo</v-btn>
									<v-btn v-else-if="hasCurrentIcon" small text color="error" @click="markIconRemoved">Remove</v-btn>
								</div>
								<p v-if="iconCleared" class="editor-icon__pending">Icon will be removed when you save.</p>
								<p v-else-if="iconFile" class="editor-icon__pending">{{ iconFile.name }} will be uploaded when you save.</p>
							</div>
							<input
								ref="fileInput"
								type="file"
								accept=".svg,.png,image/svg+xml,image/png"
								class="d-none"
								@change="onFileInputChange"
							/>
						</div>
					</section>

					<!-- Details -->
					<section class="editor-section">
						<v-text-field
							ref="nameField"
							v-model="editor.name"
							label="Name"
							outlined
							dense
							hide-details="auto"
							:rules="[(v) => !!(v && v.trim()) || 'Name is required']"
							class="mb-4"
						></v-text-field>
						<v-select
							v-model="editor.type"
							:items="categoryTypes"
							item-text="text"
							item-value="value"
							label="Type"
							outlined
							dense
							hide-details="auto"
							:rules="[(v) => !!v || 'Type is required']"
							@change="onTypeChange"
						></v-select>
					</section>

					<!-- Behaviors -->
					<section class="editor-section">
						<p class="editor-section__heading">Behaviors</p>
						<div
							v-for="behavior in behaviors"
							:key="behavior.key"
							class="behavior-row"
							:class="{ 'behavior-row--disabled': isBehaviorDisabled(behavior) }"
						>
							<div class="behavior-row__text">
								<p class="behavior-row__label">{{ behavior.label }}</p>
								<p class="behavior-row__description">{{ behavior.description }}</p>
								<p v-if="isBehaviorDisabled(behavior)" class="behavior-row__note">{{ behavior.disabledNote }}</p>
							</div>
							<v-switch
								v-model="editor[behavior.key]"
								color="primary"
								inset
								dense
								hide-details
								:disabled="isBehaviorDisabled(behavior)"
								:aria-label="behavior.label"
							></v-switch>
						</div>
					</section>
				</div>

				<footer class="category-editor__footer">
					<v-btn text @click="closeEditor" :disabled="saving">Cancel</v-btn>
					<v-btn depressed color="primary" class="editor-save" :loading="saving" :disabled="!canSave" @click="saveCategory">
						{{ editor.systemCategoryId ? "Save changes" : "Create category" }}
					</v-btn>
				</footer>
			</div>
		</v-navigation-drawer>

		<WarningModal
			v-if="deleteWarning"
			:showModal="deleteWarning"
			:message="'Are you sure you want to delete the selected system categories?'"
			:itemList="deleteCategories"
			:itemListKey="'name'"
			:cancelText="'No, Cancel'"
			:confirmText="'Yes, Delete'"
			@cancel="deleteWarning = false"
			@confirm="
				deleteCategory();
				deleteWarning = false;
			"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import WarningModal from "@/components/WarningModal.vue";
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";
import { getApiUrl } from "@/helpers";

const TYPE_LABELS = { ITEM: "Item", SUBITEM: "Sub-item", PACKAGE: "Package" };

export default {
	name: "SystemCategory",
	components: {
		WarningModal,
		SystemCategoryIcon,
	},
	data() {
		return {
			searchKeywords: "",
			searchTimeout: null,
			categories: [],
			totalCount: 0,
			deleteCategories: [],
			selectedCategories: [],
			categoryTypes: [
				{ value: "ITEM", text: "Item" },
				{ value: "SUBITEM", text: "Sub-item" },
				{ value: "PACKAGE", text: "Package" },
			],
			// One row per flag: drives both the table tags and the editor switches, with the
			// explanation next to the control instead of hidden behind a header tooltip.
			behaviors: [
				{
					key: "includeWithParentPO",
					label: "Include with parent item PO",
					tag: "Prints with parent",
					description:
						"Printed under its parent item on purchase order, packing slip, and pick ticket printouts.",
					subItemOnly: true,
					disabledNote: "Available for sub-item categories",
				},
				{
					key: "includeSiblingOnPO",
					label: "Include siblings on PO printout",
					tag: "Siblings on PO",
					description:
						"A monogram purchase order printout also lists the other same-category line items ordered for the same parent item.",
					subItemOnly: true,
					disabledNote: "Available for sub-item categories",
				},
				{
					key: "doesNotProcess",
					label: "Does not process",
					tag: "No processing",
					description:
						"Left out of order processing: no pick ticket, no purchase order, no drop ship, and shipping status shows N/A. For Item and Package types.",
					notForSubItems: true,
					disabledNote: "Not available for sub-item categories or the Package category",
				},
				{
					key: "requiresLettering",
					label: "Requires positioning and text for colors",
					tag: "Lettering",
					description:
						"Color selection on quotes and orders also asks for a letter position and text, and both are required, like the Lettering category.",
				},
				{
					key: "allowsNegativePrices",
					label: "Allows negative prices",
					tag: "Negative prices",
					description: "Prices can be entered as negative amounts, for credit and discount categories.",
				},
			],
			deleteWarning: false,
			editorOpen: false,
			editor: null,
			saving: false,
			saveError: "",
			iconFile: null,
			iconPreview: null,
			iconCleared: false,
			dragActive: false,
			tableOptions: {
				page: 1,
				itemsPerPage: 15,
				sortBy: [],
				sortDesc: [],
			},
			headers: [
				{ text: "Icon", value: "icon", sortable: false, width: 88, align: "center" },
				{ text: "Name", value: "name", width: "26%" },
				{ text: "Type", value: "type", width: "14%" },
				{ text: "Behaviors", value: "behaviors", sortable: false },
				{ text: "", value: "actions", sortable: false, align: "end", width: 72 },
			],
			footerProps: {
				"items-per-page-options": [10, 15, 25, 50, 100],
				"show-first-last-page": true,
			},
		};
	},
	apollo: {
		systemCategoriesData: {
			query: gql`
				query SystemCategories($pageOffset: Int!, $take: Int!, $order: [SystemCategorySortInput!], $where: SystemCategoryFilterInput) {
					systemCategories(
						skip: $pageOffset
						take: $take
						order: $order
						where: $where
					) {
						items {
							name
							systemCategoryId
							type
							doesNotProcess
							requiresLettering
							sequence
							includeWithParentPO
							includeSiblingOnPO
							allowsNegativePrices
							iconVersion
						}
						pageInfo {
							hasNextPage
							hasPreviousPage
						}
						totalCount
					}
				}
			`,
			variables() {
				const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
				const keyword = (this.searchKeywords || "").trim();

				let order;
				if (sortBy.length > 0) {
					const direction = sortDesc[0] ? "DESC" : "ASC";
					order = [{ [sortBy[0]]: direction }];
				} else {
					// Default sort by sequence, then name
					order = [{ sequence: "ASC" }, { name: "ASC" }];
				}

				// Build where clause - only include search filter if there's a search term
				// Note: 'type' is an enum, so we can only filter by 'name' using contains
				let where = null;
				if (keyword) {
					where = {
						name: { contains: keyword },
					};
				}

				return {
					pageOffset: (page - 1) * itemsPerPage,
					take: itemsPerPage,
					order,
					where,
				};
			},
			update(data) {
				this.totalCount = data.systemCategories.totalCount;
				return data.systemCategories.items;
			},
			result({ data, loading }) {
				if (!loading && data) {
					this.categories = data.systemCategories.items;
				}
			},
			notifyOnNetworkStatusChange: true,
			debounce: 400,
		},
	},
	computed: {
		hasCurrentIcon() {
			return !!(this.editor && this.editor.systemCategoryId && this.editor.iconVersion && !this.iconCleared);
		},
		canSave() {
			return !!(this.editor && this.editor.name && this.editor.name.trim() && this.editor.type) && !this.saving;
		},
	},
	methods: {
		typeLabel(type) {
			return TYPE_LABELS[type] || type;
		},

		enabledBehaviors(item) {
			return this.behaviors.filter((b) => item[b.key]);
		},

		isBehaviorDisabled(behavior) {
			if (!this.editor) return true;
			if (behavior.subItemOnly) return this.editor.type !== "SUBITEM";
			// Does not process is a top-level item rule and never applies to the built-in Package category (id 1)
			if (behavior.notForSubItems) return this.editor.type === "SUBITEM" || this.editor.systemCategoryId === 1;
			return false;
		},

		// ----- editor lifecycle -----

		openEditor(category) {
			this.resetIconEdit();
			this.saveError = "";
			// Work on a copy so Cancel never leaves half-edited values in the table row
			this.editor = { ...category };
			this.editorOpen = true;
			window.addEventListener("keydown", this.onEditorKeydown);
			this.$nextTick(() => {
				const field = this.$refs.nameField;
				if (field && typeof field.focus === "function") field.focus();
			});
		},

		newCategory() {
			this.openEditor({
				name: "",
				systemCategoryId: null,
				type: "",
				doesNotProcess: false,
				requiresLettering: false,
				includeWithParentPO: false,
				includeSiblingOnPO: false,
				allowsNegativePrices: false,
				sequence: 0,
				iconVersion: null,
			});
		},

		editCategory(category) {
			if (this.editorOpen) return;
			this.openEditor(category);
		},

		closeEditor() {
			if (this.saving) return;
			this.editorOpen = false;
		},

		onEditorToggle(open) {
			// The scrim click closes the drawer through v-model regardless of our guard; hold it open mid-save
			if (!open && this.saving) {
				this.$nextTick(() => (this.editorOpen = true));
				return;
			}
			// Fired for the overlay click as well as our own close; tear down once closed
			if (!open) {
				window.removeEventListener("keydown", this.onEditorKeydown);
				this.resetIconEdit();
				this.editor = null;
				this.saveError = "";
			}
		},

		onEditorKeydown(event) {
			// Vuetify's drawer only closes on scrim click; Escape should work too
			if (event.key === "Escape" && this.editorOpen) this.closeEditor();
		},

		onTypeChange(type) {
			if (type !== "SUBITEM") {
				this.editor.includeWithParentPO = false;
				this.editor.includeSiblingOnPO = false;
			} else {
				this.editor.doesNotProcess = false;
			}
		},

		// ----- icon editing -----

		openFilePicker() {
			this.$refs.fileInput?.click();
		},

		onFileInputChange(event) {
			const file = event.target.files && event.target.files[0];
			this.setPendingIcon(file || null);
			event.target.value = "";
		},

		onIconDrop(event) {
			this.dragActive = false;
			const file = event.dataTransfer?.files && event.dataTransfer.files[0];
			if (file) this.setPendingIcon(file);
		},

		setPendingIcon(file) {
			if (this.iconPreview) URL.revokeObjectURL(this.iconPreview);
			this.iconFile = file;
			this.iconPreview = file ? URL.createObjectURL(file) : null;
			this.iconCleared = false;
			this.saveError = "";
		},

		clearPendingIcon() {
			this.setPendingIcon(null);
		},

		markIconRemoved() {
			this.setPendingIcon(null);
			this.iconCleared = true;
		},

		resetIconEdit() {
			if (this.iconPreview) URL.revokeObjectURL(this.iconPreview);
			this.iconFile = null;
			this.iconPreview = null;
			this.iconCleared = false;
			this.dragActive = false;
		},

		async saveIcon(systemCategoryId) {
			if (this.iconFile) {
				const form = new FormData();
				form.append("file", this.iconFile, this.iconFile.name);
				await this.$http.put(
					getApiUrl(`/api/settings/system-categories/${systemCategoryId}/icon`),
					form,
					{ headers: { "Content-Type": "multipart/form-data" } }
				);
			} else if (this.iconCleared) {
				await this.$http.delete(getApiUrl(`/api/settings/system-categories/${systemCategoryId}/icon`));
			}
		},

		// ----- persistence -----

		async deleteCategory() {
			try {
				for (const cat of this.deleteCategories) {
					await this.$http.delete(
						getApiUrl(`/api/settings/system-categories/${cat.systemCategoryId}`)
					);
				}

				this.$apollo.queries.systemCategoriesData.refetch();
				this.deleteCategories = [];
				this.selectedCategories = [];
				this.$store.dispatch("retrievesystemCategories", this);
			} catch (error) {
				console.error("Error deleting categories:", error);
			}
		},

		async saveCategory() {
			if (!this.canSave) return;
			this.saveError = "";
			this.saving = true;

			const payload = { ...this.editor, name: this.editor.name.trim(), sequence: this.editor.sequence || 0 };
			let categoryId = payload.systemCategoryId;
			try {
				if (categoryId) {
					await this.$http.put(getApiUrl(`/api/settings/system-categories/${categoryId}`), payload);
				} else {
					const res = await this.$http.post(getApiUrl("/api/settings/system-categories"), payload);
					categoryId = res.data.systemCategoryId;
					this.editor.systemCategoryId = categoryId;
				}
			} catch (error) {
				console.error("Error saving category:", error);
				this.saveError = error.response?.data?.error || error.response?.data || "The category could not be saved.";
				this.saving = false;
				return;
			}

			try {
				await this.saveIcon(categoryId);
			} catch (error) {
				console.error("Error saving category icon:", error);
				// The category itself is saved; keep the drawer open so the icon can be fixed and retried
				this.saveError = error.response?.data?.error || "The category was saved but its icon was not.";
				this.saving = false;
				this.$apollo.queries.systemCategoriesData.refetch();
				this.$store.dispatch("retrievesystemCategories", this);
				return;
			}

			this.saving = false;
			this.editorOpen = false;
			this.$apollo.queries.systemCategoriesData.refetch();
			// Refresh the shared store so every open table picks up the new icon version
			this.$store.dispatch("retrievesystemCategories", this);
		},

		onSearchInput() {
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}

			this.searchTimeout = setTimeout(() => {
				this.tableOptions.page = 1;
			}, 400);
		},

		onSearchClear() {
			this.searchKeywords = "";
			this.tableOptions.page = 1;
		},
	},
	watch: {
		selectedCategories: {
			handler(newVal) {
				this.deleteCategories = newVal.filter((cat) => cat.systemCategoryId !== null);
			},
			deep: true,
		},
	},
	beforeDestroy() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
		window.removeEventListener("keydown", this.onEditorKeydown);
		this.resetIconEdit();
	},
};
</script>

<style lang="scss" scoped>
.system-category-page {
	display: flex;
	flex-direction: column;
	height: 100%;
}
.system-category-toolbar-card {
	border-radius: 4px 4px 0 0 !important;
	border-bottom: 1px solid $color-gray;
	.v-btn {
		text-transform: initial;
	}
}
.system-category-table-container {
	display: flex;
	flex-direction: column;
	min-height: 0;
	overflow: hidden;
}
.system-category-search {
	::v-deep .v-input__slot {
		background-color: $color-gray-light-2 !important;
	}
}

// ----- table -----
.system-category-data-table {
	border-radius: 0 0 4px 4px !important;
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	overflow: hidden;
	::v-deep .v-data-table__wrapper {
		flex: 1;
		overflow-y: auto;
		overflow-x: auto;
		tbody tr {
			cursor: pointer;
			transition: background-color 0.2s;
			&:hover {
				background-color: $color-gray-light !important;
			}
		}
	}
	::v-deep .v-data-footer {
		flex-shrink: 0;
		border-top: 1px solid $color-gray;
		font-size: 1.25rem !important;
	}
	::v-deep > .v-data-table__wrapper > table > thead > tr > th {
		font-size: 1.3rem !important;
		font-weight: 500 !important;
		letter-spacing: 0.04em;
		color: $color-gray-dark !important;
		text-transform: uppercase;
		background-color: $color-gray-light-2 !important;
		white-space: nowrap;
		padding: 12px 16px !important;
	}
	::v-deep > .v-data-table__wrapper > table > tbody > tr > td {
		height: 60px !important;
		font-size: 1.6rem !important;
		padding: 8px 16px !important;
	}
	::v-deep .v-data-footer__select .v-select__selections .v-select__selection--comma {
		font-size: 1.15rem !important;
	}
	::v-deep .v-data-table__checkbox .v-icon {
		font-size: 2rem !important;
	}
}
// The global .flex-table-icon rule already draws the 50px gray icon box used across the app;
// this wrapper only centers it in the cell so no second background shows behind it.
.table-icon-tile {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	::v-deep img {
		width: 32px;
		height: 32px;
	}
}
.category-name {
	font-weight: 500;
}
.category-type {
	color: $color-gray-dark;
}
.behavior-tags {
	display: flex;
	flex-wrap: wrap;
	gap: 0.6rem;
	align-items: center;
}
.behavior-tag {
	display: inline-block;
	padding: 0.2rem 0.9rem;
	border: 1px solid $color-gray;
	border-radius: 999px;
	font-size: 1.2rem;
	line-height: 1.8rem;
	letter-spacing: 0.02em;
	color: $color-gray-dark;
	background-color: #fff;
	white-space: nowrap;
	cursor: default;
}
.behavior-none {
	font-size: 1.3rem;
	color: $color-gray;
}

// ----- editor drawer -----
.category-editor {
	// Above the hardware-status toast (z-index 1000) so Save is never covered while editing
	z-index: 1100 !important;
}
.category-editor__inner {
	display: flex;
	flex-direction: column;
	height: 100%;
}
.category-editor__header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1.2rem;
	padding: 2rem 2.4rem 1.6rem;
	border-bottom: 1px solid $color-gray-light-2;
}
.category-editor__eyebrow {
	margin: 0 0 0.2rem;
	font-size: 1.2rem;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: $color-primary;
	font-weight: 600;
}
.category-editor__title {
	margin: 0;
	font-size: 2.2rem;
	font-weight: 500;
	line-height: 1.2;
	word-break: break-word;
	text-align: left;
}
.category-editor__body p {
	text-align: left;
}
.category-editor__body {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 2rem 2.4rem;
}
.category-editor__footer {
	display: flex;
	justify-content: flex-end;
	gap: 0.8rem;
	padding: 1.4rem 2.4rem;
	border-top: 1px solid $color-gray-light-2;
	background-color: #fff;
	.v-btn {
		text-transform: initial;
		font-size: 1.4rem;
		letter-spacing: 0.01em;
	}
}
.editor-section {
	& + & {
		margin-top: 2.8rem;
	}
}
.editor-section__heading {
	margin: 0 0 1.2rem;
	font-size: 1.2rem;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: $color-gray-dark;
	font-weight: 600;
}

// icon editor
.editor-icon {
	display: flex;
	gap: 1.8rem;
	align-items: flex-start;
}
.editor-dropzone {
	flex: none;
	width: 112px;
	height: 112px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 2px dashed $color-gray;
	border-radius: 12px;
	background-color: $color-gray-light;
	cursor: pointer;
	transition: border-color 0.15s, background-color 0.15s, transform 0.15s;
	&:hover,
	&:focus-visible {
		border-color: $color-primary;
		outline: none;
	}
	&--active {
		border-color: $color-primary;
		background-color: rgba($color-primary, 0.06);
		transform: scale(1.02);
	}
	&--empty {
		border-style: dashed;
	}
	// The dashed tile is the frame here, so strip the global gray icon box from the embedded component
	::v-deep .flex-table-icon {
		background-color: transparent;
		border: 0;
		padding: 0;
		min-width: 0;
	}
	::v-deep img,
	&__image {
		width: 72px;
		height: 72px;
		object-fit: contain;
	}
	&__placeholder {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.4rem;
		font-size: 1.2rem;
		color: $color-gray-dark;
	}
}
.editor-icon__controls {
	flex: 1;
	min-width: 0;
}
.editor-icon__label {
	margin: 0 0 0.2rem;
	font-size: 1.2rem;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: $color-gray-dark;
	font-weight: 600;
}
.editor-icon__hint {
	margin: 0 0 1rem;
	font-size: 1.3rem;
	line-height: 1.8rem;
	color: $color-gray-dark;
	text-align: left;
}
.editor-icon__buttons {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.4rem;
	.v-btn {
		text-transform: initial;
		font-size: 1.3rem;
		letter-spacing: 0.01em;
	}
}
.editor-icon__pending {
	margin: 0.8rem 0 0;
	font-size: 1.2rem;
	color: $color-primary;
}

// behaviors
.behavior-row {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1.6rem;
	padding: 1.2rem 0;
	border-top: 1px solid $color-gray-light-2;
	&:last-child {
		border-bottom: 1px solid $color-gray-light-2;
	}
	&--disabled {
		opacity: 0.5;
	}
	::v-deep .v-input--switch {
		margin: 0;
		padding: 0;
		flex: none;
	}
}
.behavior-row__text {
	min-width: 0;
}
.behavior-row__label {
	margin: 0;
	font-size: 1.5rem;
	font-weight: 500;
	line-height: 2rem;
}
.behavior-row__description {
	margin: 0.2rem 0 0;
	font-size: 1.3rem;
	line-height: 1.8rem;
	color: $color-gray-dark;
	text-align: left;
}
.behavior-row__note {
	margin: 0.3rem 0 0;
	font-size: 1.1rem;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: $color-gray;
}

@media (prefers-reduced-motion: reduce) {
	.editor-dropzone,
	.system-category-data-table ::v-deep .v-data-table__wrapper tbody tr {
		transition: none;
	}
}
</style>
